import React, {useCallback, useEffect, useState, useMemo} from "react";
import {useParams} from "react-router-dom";
import {Modal} from "react-bootstrap";
import { currentRoofPlanBlueprint, roofPlanComponents } from "../../Helpers/roofPlan";
import { hasPhotoLocation } from "../../Helpers/photoLocations";
import {
    useBuildingKey,
    getAllUniqueRiskTypesFromBuilding,
    countWindowsForComponent,
    countDoorsForComponent,
    countSheetMetalForComponent,
    countChimneysForComponent,
    countDrainageForComponent,
    countUtilitiesForComponent,
    countBalconiesForComponent,
    countMiscellaneousForComponent,
} from "../../Helpers/utils";
import {BreadcrumbComponent} from "../../Components/BreadCrumb";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { useIsAdmin } from "../../hooks/useIsAdmin";
import {BaseType, UtilityCategory, OutdoorCategory} from "../../types/Enums";
import { totalLinearMeters } from "../../Helpers/baseTypeUtils";
import {translate} from "../../types/EnumTranslations";
import InfoPanel, {InfoItem} from "../../Components/InfoPanel";
import ClickableImage from "../../Components/ClickableImage";
import {BaseComponentImpl} from "../../types/implementations";
import {useBuilding} from "../../Helpers/BuildingContext";
import {ChevronUp, ChevronDown, Images, PencilRuler, Map as MapIcon, List, LucideIcon} from "lucide-react";
import ListView from "./components/ListView";
import BlueprintView from "./components/BlueprintView";
import RoofDrawingSection from "./components/RoofDrawingSection";
import MapView from "./components/MapView";
import PhotoView from "./components/PhotoView";
import ComponentDetailPane from "./components/ComponentDetailPane";
import CustomDropdown, {CustomDropdownOption} from "../../Components/Dropdown";
import "./GenericComponentListPage.css";
import { PageLoader } from "../../Components/LoadingSpinner";
import { useIsSmallScreen } from "../../hooks/useIsSmallScreen";

// ── View Mode Types ──────────────────────────────────────────────

type ViewMode = 'photo' | 'blueprint' | 'map' | 'list';

const VIEW_MODE_LABELS: Record<ViewMode, string> = {
    photo: 'Fasad/tak',
    blueprint: 'Ritning',
    map: 'Karta',
    list: 'Lista',
};

const VIEW_MODE_ICONS: Record<ViewMode, LucideIcon> = {
    photo: Images,
    blueprint: PencilRuler,
    map: MapIcon,
    list: List,
};

const VIEW_MODE_PRIORITY: ViewMode[] = ['photo', 'blueprint', 'map', 'list'];
/** Roofs: the satellite image comes before the roof drawing, because most buildings will have a
 *  roof drawing long before anything is positioned on it, while satellite positions exist from the start. */
const ROOF_VIEW_MODE_PRIORITY: ViewMode[] = ['photo', 'map', 'blueprint', 'list'];

// ── Grouping Strategy ────────────────────────────────────────────

export type GroupingStrategy = 'direction' | 'parentSurface' | 'room' | 'doorCategory' | 'dimensions' | 'roofPlan' | 'none';

// ── List Sort (windows only) ─────────────────────────────────────

/** How the "Lista" tab groups windows: by placement (parent facade/room) or by modulmått. */
type WindowListSort = 'placement' | 'dimensions';

const WINDOW_LIST_SORT_OPTIONS: CustomDropdownOption<WindowListSort>[] = [
    { key: 'placement', name: 'Placering' },
    { key: 'dimensions', name: 'Modulmått' },
];

function groupingStrategyForType(type: BaseType): GroupingStrategy {
    switch (type) {
        case BaseType.Roof:
            return 'roofPlan';
        case BaseType.Facade:
            return 'direction';
        case BaseType.RoofDrainage:
        case BaseType.SheetMetal:
        case BaseType.Chimney:
        case BaseType.Window:
        case BaseType.Balcony:
        case BaseType.Miscellaneous:
            return 'parentSurface';
        case BaseType.Door:
            return 'doorCategory';
        case BaseType.Utility:
        case BaseType.FixedFurnishing:
            return 'room';
        default:
            return 'none';
    }
}

// ── Helpers ──────────────────────────────────────────────────────

const UNCATEGORIZED_SEGMENT = "uncategorized";

function isValidUtilityCategory(value: string): value is UtilityCategory {
    return Object.values(UtilityCategory).includes(value as UtilityCategory);
}

function isValidOutdoorCategory(value: string): value is OutdoorCategory {
    return Object.values(OutdoorCategory).includes(value as OutdoorCategory);
}

function isValidCategoryRoute(value: string): boolean {
    return value === UNCATEGORIZED_SEGMENT || isValidUtilityCategory(value) || isValidOutdoorCategory(value);
}

// ── Component ────────────────────────────────────────────────────

interface GenericComponentPageProps {
    type: BaseType;
}

const GenericComponentListPage: React.FC<GenericComponentPageProps> = ({type}) => {
    const { category } = useParams<{ category?: string }>();
    const isUncategorized = category === UNCATEGORIZED_SEGMENT;
    const utilityCategory = category && isValidUtilityCategory(category) ? category : undefined;
    const outdoorCategory = category && isValidOutdoorCategory(category) ? category : undefined;
    const activeCategory = utilityCategory || outdoorCategory;

    const pageTitle = isUncategorized ? "Okategoriserat" : activeCategory ? translate(activeCategory) : translate(type);
    const listGrouping = groupingStrategyForType(type);
    const buildingKey = useBuildingKey();
    const { currentBuilding, buildingError, loadingBuilding, fetchBuilding } = useBuilding();
    const isAdmin = useIsAdmin();

    // The roof page IS the takplan (plan D6): its set is every roof plus what
    // sits on one, its "Ritning" tab is the roof drawing at structure/roofPlan.
    const isRoofPage = type === BaseType.Roof;

    // Set document title based on state
    const isCategoryInvalid = category && !isValidCategoryRoute(category);
    useDocumentTitle(
        loadingBuilding ? 'Laddar...' :
        buildingError ? 'Fel' :
        !buildingKey ? 'Komponentlista' :
        !currentBuilding ? 'Komponentlista' :
        isCategoryInvalid ? '404 - Kategori hittades inte' :
        `${pageTitle}: ${currentBuilding.summary?.name || 'Byggnad'}`
    );

    // Non-list views (Blueprint, Map, Photo) use parentSurface for doors
    // instead of doorCategory, since those views don't support the split.
    const otherViewGrouping: GroupingStrategy = listGrouping === 'doorCategory' ? 'parentSurface' : listGrouping;

    // View mode state. The chosen tab is kept only while it is still offered;
    // otherwise (and before any choice) the page falls back to the default.
    const [chosenMode, setChosenMode] = useState<ViewMode | null>(null);
    const [selectedComponentKey, setSelectedComponentKey] = useState<string | null>(null);
    const [hoveredComponentKey, setHoveredComponentKey] = useState<string | null>(null);
    const [faktaOpen, setFaktaOpen] = useState(true);
    const [windowListSort, setWindowListSort] = useState<WindowListSort>('placement');

    // A new page (another base type or category) starts over.
    useEffect(() => {
        setChosenMode(null);
        setSelectedComponentKey(null);
        setHoveredComponentKey(null);
    }, [type, category]);

    useEffect(() => {
        const loadBuildingData = async () => {
            if (buildingKey) {
                await fetchBuilding(buildingKey);
            }
        };
        loadBuildingData().then();
    }, [buildingKey, fetchBuilding]);

    // A deleted component also changes the components it detached, so re-read the building
    const handleComponentDeleted = useCallback(() => {
        setSelectedComponentKey(null);
        if (buildingKey) fetchBuilding(buildingKey).then();
    }, [buildingKey, fetchBuilding]);

    // Derive filtered components
    const components = useMemo(() => {
        if (!currentBuilding?.model) return null;
        const structure = currentBuilding.model.structure;
        if (isRoofPage) {
            return roofPlanComponents(structure).map((item) => new BaseComponentImpl(item));
        }
        return Object.values(
            structure?.components ?? []
        ).filter((component) => {
            // Skip archived components
            if (component.archivedAt) return false;
            if (component.type !== type) return false;
            if (!activeCategory && !isUncategorized) return true;

            if (type === BaseType.Outdoor) {
                if (isUncategorized) {
                    return !component.outdoorCategory || !isValidOutdoorCategory(component.outdoorCategory);
                }
                return component.outdoorCategory === outdoorCategory;
            }

            // Utility category filtering
            if (isUncategorized) {
                return !component.utilityCategory || !isValidUtilityCategory(component.utilityCategory);
            }
            return component.utilityCategory === utilityCategory;
        })
        .map((item) => new BaseComponentImpl(item));
    }, [currentBuilding, type, activeCategory, utilityCategory, outdoorCategory, isUncategorized, isRoofPage]);

    // The components of the page's own base type. On the roof page these are
    // the roofs: the facts panel counts them and "Fasad/tak" shows their photos
    // (mounted components keep their photos on their own pages).
    const ownTypeComponents = useMemo(() => {
        if (!components) return null;
        return isRoofPage ? components.filter(component => component.type === BaseType.Roof) : components;
    }, [components, isRoofPage]);

    // Extract for narrower dependency
    const mapImageKey = currentBuilding?.model?.currentMapImageKey;
    const roofDrawing = useMemo(
        () => (isRoofPage ? currentRoofPlanBlueprint(currentBuilding?.model.structure) : null),
        [isRoofPage, currentBuilding]
    );
    // The roof drawing tab exists without a drawing only so an admin can upload the first one.
    const blueprintIsUploadOnly = isRoofPage && !roofDrawing && isAdmin;

    // Derive available view modes from component data
    const availableModes = useMemo<ViewMode[]>(() => {
        const all = components ?? [];
        const own = ownTypeComponents ?? [];

        const hasBlueprint = isRoofPage
            ? roofDrawing !== null || isAdmin
            : all.some(c => c.blueprintLocations && Object.keys(c.blueprintLocations).length > 0);
        const hasMap = !!mapImageKey && all.some(c =>
            c.mapLocations && c.mapLocations[mapImageKey]
        );
        const hasPhoto = own.some(c => hasPhotoLocation(c));

        const offered: Record<ViewMode, boolean> = { photo: hasPhoto, blueprint: hasBlueprint, map: hasMap, list: true };
        const order = isRoofPage ? ROOF_VIEW_MODE_PRIORITY : VIEW_MODE_PRIORITY;
        return order.filter(mode => offered[mode]);
    }, [components, ownTypeComponents, mapImageKey, isRoofPage, roofDrawing, isAdmin]);

    const defaultMode = (isRoofPage ? ROOF_VIEW_MODE_PRIORITY : VIEW_MODE_PRIORITY).find(mode =>
        availableModes.includes(mode) && !(mode === 'blueprint' && blueprintIsUploadOnly)
    ) ?? 'list';
    const activeMode: ViewMode = chosenMode && availableModes.includes(chosenMode) ? chosenMode : defaultMode;

    const showWindowListSort = type === BaseType.Window && activeMode === 'list';
    const effectiveListGrouping: GroupingStrategy =
        showWindowListSort && windowListSort === 'dimensions' ? 'dimensions' : listGrouping;

    const viewModeLabel = (mode: ViewMode): string =>
        mode === 'blueprint' && isRoofPage ? 'Takritning' : VIEW_MODE_LABELS[mode];

    const handleModeChange = (mode: ViewMode) => {
        setChosenMode(mode);
        setSelectedComponentKey(null);
        setHoveredComponentKey(null);
    };

    const handleComponentClick = (componentKey: string) => {
        const isDeselecting = selectedComponentKey === componentKey;
        setSelectedComponentKey(isDeselecting ? null : componentKey);
        if (!isDeselecting) setFaktaOpen(false);
    };

    // Resolve selected component
    // Phones stack the panes, so a tapped component opens in a modal
    // instead of the detail pane below the list.
    const isSmallScreen = useIsSmallScreen();

    const selectedComponent = useMemo(() => {
        if (!selectedComponentKey || !components) return null;
        return components.find(c => c.key === selectedComponentKey) ?? null;
    }, [selectedComponentKey, components]);

    // ── Memoized Fakta panel data ─────────────────────────────────
    const infoItems = useMemo(() => {
        const counted = ownTypeComponents;
        if (!counted || !currentBuilding) return [];

        const items: InfoItem[] = [];
        items.push({ label: "Typ", value: translate(type) });
        items.push({ label: "Antal", value: counted.length });

        const totalArea = counted.reduce((sum, component) => {
            const area = component.totalArea(currentBuilding);
            return area != null ? sum + area : sum;
        }, 0);
        // Stammar are measured by length and have no area; Övrigt has neither
        if (type === BaseType.Riser) {
            items.push({ label: "Total längd", value: `${totalLinearMeters(counted).toFixed(1)} lm` });
        } else if (type !== BaseType.Miscellaneous) {
            items.push({ label: "Total area", value: `${totalArea.toFixed(1)} kvm` });
        }

        // Count child windows/doors using existing utility functions
        let windowCount = 0;
        let doorCount = 0;
        counted.forEach(component => {
            windowCount += countWindowsForComponent(component.key, currentBuilding);
            doorCount += countDoorsForComponent(component.key, currentBuilding);
        });

        // Calculate combined window/door area
        let totalWindowsAndDoorsArea = 0;
        if (windowCount > 0 || doorCount > 0) {
            const allComponents = currentBuilding.model.structure?.components;
            if (allComponents) {
                Object.values(allComponents).forEach(comp => {
                    if (comp.type === BaseType.Window || comp.type === BaseType.Door) {
                        const parentSurfaces = [
                            comp.parentSurface?.value,
                            comp.parentSurface1?.value,
                            comp.parentSurface2?.value
                        ].filter(Boolean);
                        if (counted.some(c => parentSurfaces.includes(c.key))) {
                            const childImpl = new BaseComponentImpl(comp);
                            const childArea = childImpl.totalArea(currentBuilding);
                            if (childArea) totalWindowsAndDoorsArea += childArea;
                        }
                    }
                });
            }
        }

        if (windowCount > 0) items.push({ label: "Fönster", value: `${windowCount} st` });
        if (doorCount > 0) items.push({ label: "Dörrar", value: `${doorCount} st` });
        if (totalArea && totalWindowsAndDoorsArea > 0.01) {
            const areaExcl = totalArea - totalWindowsAndDoorsArea;
            if (Math.abs(totalArea - areaExcl) > 0.01) {
                items.push({ label: "Area exkl fönster/dörrar", value: `${areaExcl.toFixed(1)} kvm` });
            }
        }

        // Count sheet metal, chimneys, drainage and mounted utilities for roof/facade types
        if (type === BaseType.Roof || type === BaseType.Facade) {
            let sheetMetalCount = 0;
            let chimneyCount = 0;
            let drainageCount = 0;
            let utilityCount = 0;
            let balconyCount = 0;
            let miscellaneousCount = 0;
            counted.forEach(component => {
                sheetMetalCount += countSheetMetalForComponent(component.key, currentBuilding);
                chimneyCount += countChimneysForComponent(component.key, currentBuilding);
                drainageCount += countDrainageForComponent(component.key, currentBuilding);
                utilityCount += countUtilitiesForComponent(component.key, currentBuilding);
                balconyCount += countBalconiesForComponent(component.key, currentBuilding);
                miscellaneousCount += countMiscellaneousForComponent(component.key, currentBuilding);
            });
            if (sheetMetalCount > 0) items.push({ label: translate(BaseType.SheetMetal), value: `${sheetMetalCount} st` });
            if (chimneyCount > 0) items.push({ label: translate(BaseType.Chimney), value: `${chimneyCount} st` });
            if (drainageCount > 0) items.push({ label: translate(BaseType.RoofDrainage), value: `${drainageCount} st` });
            if (utilityCount > 0) items.push({ label: translate(BaseType.Utility), value: `${utilityCount} st` });
            if (balconyCount > 0) items.push({ label: translate(BaseType.Balcony), value: `${balconyCount} st` });
            if (miscellaneousCount > 0) items.push({ label: translate(BaseType.Miscellaneous), value: `${miscellaneousCount} st` });
        }

        // Risk constructions
        const riskConstructions = getAllUniqueRiskTypesFromBuilding({
            model: {
                structure: {
                    components: counted.reduce((acc, component) => {
                        acc[component.key] = component;
                        return acc;
                    }, {} as { [key: string]: any })
                }
            }
        });
        if (riskConstructions.length > 0) {
            items.push({ label: "Riskkonstruktioner", value: riskConstructions.map(r => r.riskType).join(', ') });
        }

        return items;
    }, [ownTypeComponents, currentBuilding, type]);

    // Early returns after all hooks
    if (loadingBuilding) return <PageLoader />;
    if (buildingError) return <p style={{ color: "red" }}>{buildingError}</p>;
    if (!buildingKey) return <p>Missing building key</p>;
    if (!currentBuilding) return <p>Building data missing</p>;

    if (category && !isValidCategoryRoute(category)) {
        return <p>404 - Kategori hittades inte</p>;
    }

    const { model, summary } = currentBuilding;

    // ── Render ───────────────────────────────────────────────────

    const hasComponents = !!components && components.length > 0;
    const showTabs = availableModes.length > 1;

    const renderActiveView = () => {
        // The roof drawing is reachable with no components too, so the first upload can happen here.
        if (isRoofPage && activeMode === 'blueprint') {
            return (
                <RoofDrawingSection
                    building={currentBuilding}
                    buildingKey={buildingKey}
                    components={components ?? []}
                    selectedKey={selectedComponentKey}
                    onComponentClick={handleComponentClick}
                    isAdmin={isAdmin}
                />
            );
        }

        if (!components || components.length === 0) {
            return (
                <p>{(activeCategory || isUncategorized) ? "Inga komponenter i denna kategori" : "Inga byggdelar"}</p>
            );
        }

        switch (activeMode) {
            case 'list':
                return (
                    <ListView
                        components={components}
                        building={currentBuilding}
                        buildingKey={buildingKey}
                        selectedKey={selectedComponentKey}
                        onComponentClick={handleComponentClick}
                        groupingStrategy={effectiveListGrouping}
                    />
                );
            case 'blueprint':
                return (
                    <BlueprintView
                        components={components}
                        building={currentBuilding}
                        buildingKey={buildingKey}
                        selectedKey={selectedComponentKey}
                        hoveredKey={hoveredComponentKey}
                        onComponentClick={handleComponentClick}
                        onComponentHover={setHoveredComponentKey}
                    />
                );
            case 'map':
                return (
                    <MapView
                        components={components}
                        building={currentBuilding}
                        buildingKey={buildingKey}
                        selectedKey={selectedComponentKey}
                        hoveredKey={hoveredComponentKey}
                        onComponentClick={handleComponentClick}
                        onComponentHover={setHoveredComponentKey}
                        groupingStrategy={otherViewGrouping}
                    />
                );
            case 'photo':
                return (
                    <PhotoView
                        components={ownTypeComponents ?? components}
                        building={currentBuilding}
                        buildingKey={buildingKey}
                        selectedKey={selectedComponentKey}
                        hoveredKey={hoveredComponentKey}
                        onComponentClick={handleComponentClick}
                        onComponentHover={setHoveredComponentKey}
                        componentType={type}
                    />
                );
        }
    };

    return (
        <>
            <BreadcrumbComponent
                items={[
                    {text: summary.name ?? 'Byggnad', link: `/building/${buildingKey}`},
                    {text: pageTitle}
                ]}
            />
            <div className="generic-component-page">
                {/* Tab bar */}
                {showTabs && (
                    <div className="generic-component-tab-bar">
                        {availableModes.map(mode => {
                            const Icon = VIEW_MODE_ICONS[mode];
                            return (
                                <button
                                    key={mode}
                                    type="button"
                                    onClick={() => handleModeChange(mode)}
                                    className={`generic-component-tab ${activeMode === mode ? 'active' : ''}`}
                                >
                                    <Icon size={18} aria-hidden="true" />
                                    {viewModeLabel(mode)}
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Two-pane layout */}
                <div className="generic-component-layout">
                    {/* Left: active view */}
                    <div className="generic-component-main">
                        {showWindowListSort && hasComponents && (
                            <div className="generic-component-list-toolbar">
                                <span className="generic-component-list-toolbar-label">Sortera på</span>
                                <CustomDropdown<WindowListSort>
                                    options={WINDOW_LIST_SORT_OPTIONS}
                                    selectedKey={windowListSort}
                                    onSelect={setWindowListSort}
                                    showAllOption={false}
                                    icon="pi pi-sort-alt"
                                />
                            </div>
                        )}
                        {renderActiveView()}
                    </div>

                    {/* Right: Fakta + detail pane */}
                    <div className="generic-component-sidebar">
                        <div className="dropshadow generic-component-fakta-panel">
                            <h3
                                onClick={() => setFaktaOpen(prev => !prev)}
                                className="generic-component-fakta-header"
                            >
                                Fakta
                                {faktaOpen ? <ChevronUp size={18} color="#999" /> : <ChevronDown size={18} color="#999" />}
                            </h3>
                            {faktaOpen && <div className="generic-component-fakta-content"><InfoPanel items={infoItems} /></div>}
                            {
                                faktaOpen &&
                                (listGrouping === 'direction' || listGrouping === 'roofPlan') &&
                                model.mapImages &&
                                <div className="generic-component-map-container">
                                    <div className="generic-component-map-wrapper">
                                        <div className="generic-component-map-image">
                                            <ClickableImage
                                                buildingKey={model.key}
                                                photo={model.mapImages[model.currentMapImageKey ?? ""]}
                                                size={260}
                                                showDate={false}
                                            />
                                        </div>
                                        <div className="generic-component-compass-overlay">
                                            <img
                                                src="/Images/UI/compass.png"
                                                alt={"Compass"}
                                                className="generic-component-compass-img"
                                            />
                                        </div>
                                    </div>
                                    {model.mapImages[model.currentMapImageKey ?? ""]?.date && (
                                        <div className="generic-component-map-date">
                                            {model.mapImages[model.currentMapImageKey ?? ""].date.split(" ")[0]}
                                        </div>
                                    )}
                                </div>
                            }
                        </div>

                        {!isSmallScreen && (
                            <ComponentDetailPane
                                selectedComponent={selectedComponent}
                                building={currentBuilding}
                                buildingKey={buildingKey}
                                hasComponents={hasComponents}
                                onDeleted={handleComponentDeleted}
                            />
                        )}
                    </div>
                </div>
            </div>

            <Modal
                show={isSmallScreen && !!selectedComponent}
                onHide={() => setSelectedComponentKey(null)}
                scrollable
            >
                <Modal.Header closeButton>
                    <Modal.Title>Komponent</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ComponentDetailPane
                        selectedComponent={selectedComponent}
                        building={currentBuilding}
                        buildingKey={buildingKey}
                        hasComponents
                        inModal
                        onDeleted={handleComponentDeleted}
                    />
                </Modal.Body>
            </Modal>
        </>
    );
};

export default GenericComponentListPage;
